<template>
  <button :type="type" :class="[baseClass, sizeClass, toneClass, pressedClass]">
    <slot />
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    size?: 'control' | 'md' | 'session-menu' | 'sm' | 'toolbar';
    tone?: 'gold' | 'muted' | 'neutral';
    type?: 'button' | 'submit' | 'reset';
    variant?: 'default' | 'toolbar';
  }>(),
  {
    size: 'md',
    tone: 'neutral',
    type: 'button',
    variant: 'default',
  },
);

const baseClass = computed(() => {
  const widthClass = props.size === 'session-menu' ? 'w-10.5' : 'aspect-square';

  return [
    'inline-flex shrink-0 items-center justify-center rounded-[1px] border shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] transition enabled:active:translate-y-px focus-visible:outline-none focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-60',
    widthClass,
  ];
});

const sizeClass = computed(() => {
  switch (props.size) {
    case 'control':
      return 'h-6.5 text-[11px]';
    case 'session-menu':
      return 'h-6.25 text-[11px]';
    case 'toolbar':
      return 'size-7.5 text-[11px]';
    case 'sm':
      return 'size-5.25 text-[10px]';
    default:
      return 'size-6.25 text-[11px]';
  }
});

const pressedClass = computed(() => [
  'aria-pressed:border-conference-accent-border aria-pressed:bg-conference-control-pressed aria-pressed:text-conference-control-highlight aria-pressed:shadow-[inset_0_1px_0_rgba(255,255,255,0.04),inset_0_0_0_1px_rgba(0,0,0,0.24)]',
  'aria-pressed:enabled:hover:border-conference-accent-border aria-pressed:enabled:hover:bg-conference-control-pressed aria-pressed:enabled:hover:text-conference-control-highlight',
]);

const toneClass = computed(() => {
  if (props.variant === 'toolbar') {
    return 'border-transparent bg-transparent text-white shadow-none enabled:hover:border-conference-accent-border enabled:hover:bg-transparent enabled:hover:text-conference-control-highlight';
  }

  switch (props.tone) {
    case 'gold':
      return 'border-conference-accent-border bg-conference-accent text-conference-accent-text enabled:hover:border-conference-accent-border enabled:hover:bg-conference-accent focus-visible:ring-conference-accent/30';
    case 'muted':
      return 'border-conference-panel-border bg-conference-panel text-conference-muted enabled:hover:text-conference-shell-foreground focus-visible:ring-slate-500/30';
    default:
      return 'border-conference-panel-border bg-conference-control-fill text-white enabled:hover:border-conference-accent-border enabled:hover:text-conference-control-highlight focus-visible:ring-conference-accent/25';
  }
});
</script>
